import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Percent, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { platformName } from "@/components/AccountingSync";
import { EmptyState, HeaderRow, LoadingState, TH, TableShell } from "@/components/PageUi";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { isExpense, isIncome, sortBy } from "@/lib/accounting-utils";
import { apiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";

type ArSettings = components["schemas"]["ArAccountingSettingsResponse"];
type ApSettings = components["schemas"]["ApAccountingSettingsResponse"];
export type LedgerAccount = components["schemas"]["LedgerAccountResponse"];
export type PlatformTaxRate = components["schemas"]["AccountingTaxRateResponse"];
type Mapping = components["schemas"]["TaxRateMappingResponse"];

const UNMAPPED = "__unmapped__";

interface SectionProps {
  /** Active accounts synced from the platform. */
  accounts: LedgerAccount[];
  /** Active tax rates synced from the platform. */
  taxRates: PlatformTaxRate[];
  platform?: string | null;
  /** Owners and Admins choose these; anyone else sees them read-only rather than meeting a refusal on save. */
  canEdit: boolean;
}

// Nothing is pushed until the income account is chosen, so that choice is the switch, and the section says so.
export function ReceivablesAccountingSection({ accounts, taxRates, platform: platformId, canEdit }: SectionProps) {
  const api = useDolfinClient();
  const [settings, setSettings] = useState<ArSettings | null>(null);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savingRate, setSavingRate] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [settingsResult, mappingsResult] = await Promise.all([
      api.GET("/v1/accounting/receivables-settings", {}),
      api.GET("/v1/accounting/tax-rate-mappings", {}),
    ]);
    setSettings(settingsResult.data ?? null);
    setMappings(mappingsResult.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  async function saveAccount(field: "defaultIncomeLedgerAccountId" | "depositLedgerAccountId", id: string) {
    setBusy(true);
    const { data, error } = await api.PATCH("/v1/accounting/receivables-settings", { body: { [field]: id } });
    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't save the account."));
    } else {
      setSettings(data);
      toast.success(field === "defaultIncomeLedgerAccountId" ? "Income account saved." : "Deposit account saved.");
    }
    setBusy(false);
  }

  async function saveMapping(taxRateId: string, accountingTaxRateId: string) {
    setSavingRate(taxRateId);
    const { error } =
      accountingTaxRateId === UNMAPPED
        ? await api.DELETE("/v1/accounting/tax-rate-mappings/{taxRateId}", { params: { path: { taxRateId } } })
        : await api.PUT("/v1/accounting/tax-rate-mappings/{taxRateId}", {
            params: { path: { taxRateId } },
            body: { accountingTaxRateId },
          });
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't save the tax rate mapping."));
    } else {
      const { data } = await api.GET("/v1/accounting/tax-rate-mappings", {});
      setMappings(data ?? []);
    }
    setSavingRate(null);
  }

  // Revenue accounts first: the platforms classify them, and an income account is what belongs here. The rest
  // stay pickable, because charts are not always classified the way we would expect.
  const incomeAccounts = useMemo(() => sortBy(accounts, isIncome), [accounts]);
  const bankAccounts = useMemo(
    () => accounts.filter((a) => !a.accountType || a.accountType.toLowerCase() === "bank"),
    [accounts],
  );
  // Sales rates first. Xero types them OUTPUT; QuickBooks leaves type empty, so those stay in the middle.
  const sortedPlatformRates = useMemo(
    () => sortBy(taxRates, (r) => (r.type ?? "").toUpperCase().startsWith("OUTPUT")),
    [taxRates],
  );

  const platform = platformName(platformId);
  const unmapped = mappings.filter((m) => !m.accountingTaxRateId).length;
  const live = Boolean(settings?.defaultIncomeLedgerAccountId);

  return (
    <SettingsSection loading={loading}>
      <StatusCard
        canEdit={canEdit}
        live={live}
        liveTitle={`Issued invoices go to ${platform}`}
        idleTitle={`Not pushing invoices to ${platform} yet`}
        liveText="Each invoice is pushed when it is issued, with its customer. Payments, credit notes and voids follow it. Nothing already in your books is edited."
        idleText="Choose the income account your revenue posts to. Invoices are pushed from the moment you do; earlier ones can be pushed from the invoice page."
      >
        <AccountPicker
          id="income-account"
          label="Income account"
          hint="Where invoice revenue posts, unless a product names its own. Nothing is pushed until it is set."
          value={settings?.defaultIncomeLedgerAccountId}
          accounts={incomeAccounts}
          disabled={busy || !canEdit}
          onChange={(id) => saveAccount("defaultIncomeLedgerAccountId", id)}
        />
        <AccountPicker
          id="deposit-account"
          label="Deposit account"
          hint="The bank account invoice payments are recorded as landing in. Payments wait until it is set."
          value={settings?.depositLedgerAccountId}
          accounts={bankAccounts}
          disabled={busy || !canEdit}
          onChange={(id) => saveAccount("depositLedgerAccountId", id)}
        />
      </StatusCard>

      <div className="mt-6 mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Tax rates</h3>
          <p className="text-sm text-muted-foreground">
            Which of {platform}'s rates each of yours posts as. Choose sales rates, never purchase ones.
          </p>
        </div>
        {unmapped > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
            <TriangleAlert className="size-3.5" /> {unmapped} not mapped
          </span>
        ) : null}
      </div>

      {mappings.length === 0 ? (
        <EmptyState icon={Percent} title="No tax rates">
          Your organisation has no tax rates yet, so invoices carry none to map.
        </EmptyState>
      ) : (
        <TableShell>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Your rate</TableHead>
                <TableHead className={cn(TH, "text-right")}>Rate</TableHead>
                <TableHead className={TH}>Posts as</TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {mappings.map((m) => (
                <TableRow key={m.taxRateId}>
                  <TableCell>
                    <div className="font-semibold">{m.taxRateName}</div>
                    <div className="font-mono text-xs text-muted-foreground">{m.taxRateCode}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{Number(m.taxRate)}%</TableCell>
                  <TableCell className="w-[22rem]">
                    <div className="flex items-center gap-2">
                      <Select
                        value={m.accountingTaxRateId ?? UNMAPPED}
                        onValueChange={(v) => saveMapping(m.taxRateId!, v)}
                        disabled={savingRate === m.taxRateId || !canEdit}
                      >
                        <SelectTrigger
                          className={cn("w-full", !m.accountingTaxRateId && "border-amber-500/60 text-muted-foreground")}
                          aria-label={`Platform rate for ${m.taxRateName}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNMAPPED}>Not mapped</SelectItem>
                          {sortedPlatformRates.map((r) => (
                            <SelectItem key={r.id} value={r.id!}>
                              {taxRateLabel(r)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {savingRate === m.taxRateId ? <Spinner className="size-4 text-muted-foreground" /> : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      )}
    </SettingsSection>
  );
}

// Like invoices, nothing is pushed until the default account is chosen.
export function PayablesAccountingSection({ accounts, platform: platformId, canEdit }: SectionProps) {
  const api = useDolfinClient();
  const [settings, setSettings] = useState<ApSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.GET("/v1/accounting/payables-settings", {}).then(({ data }) => {
      setSettings(data ?? null);
      setLoading(false);
    });
  }, [api]);

  async function saveAccount(field: "defaultExpenseLedgerAccountId" | "billPaymentLedgerAccountId", id: string) {
    setBusy(true);
    const { data, error } = await api.PATCH("/v1/accounting/payables-settings", { body: { [field]: id } });
    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't save the account."));
    } else {
      setSettings(data);
      toast.success(field === "defaultExpenseLedgerAccountId" ? "Default expense account saved." : "Bill payment account saved.");
    }
    setBusy(false);
  }

  const expenseAccounts = useMemo(() => sortBy(accounts, isExpense), [accounts]);
  // Only these are accepted: a bill is paid out of a bank or a credit card account.
  const paymentAccounts = useMemo(() => accounts.filter(isPaymentAccount), [accounts]);

  const platform = platformName(platformId);
  const live = Boolean(settings?.defaultExpenseLedgerAccountId);

  return (
    <SettingsSection loading={loading}>
      <StatusCard
        canEdit={canEdit}
        live={live}
        liveTitle={`Approved bills go to ${platform}`}
        idleTitle={`Not pushing bills to ${platform} yet`}
        liveText="Each bill is pushed when it is approved, with its supplier and its PDF. Payments and supplier credit follow it. Lines post to their own account, else the supplier's, else this default."
        idleText="Choose the account bill lines post to by default. Bills are pushed from the moment you do; ones approved earlier can be pushed from the bill page."
      >
        <AccountPicker
          id="default-expense-account"
          label="Default expense account"
          hint="Where a bill line posts when neither the line nor its supplier names an account. Nothing is pushed until it is set."
          value={settings?.defaultExpenseLedgerAccountId}
          accounts={expenseAccounts}
          disabled={busy || !canEdit}
          onChange={(id) => saveAccount("defaultExpenseLedgerAccountId", id)}
        />
        <AccountPicker
          id="bill-payment-account"
          label="Bill payment account"
          hint="The bank or credit card account bills are recorded as paid from. Payments wait until it is set."
          value={settings?.billPaymentLedgerAccountId}
          accounts={paymentAccounts}
          emptyLabel="No bank or card accounts synced"
          disabled={busy || !canEdit}
          onChange={(id) => saveAccount("billPaymentLedgerAccountId", id)}
        />
      </StatusCard>
    </SettingsSection>
  );
}

// Each section is the whole of its area's Accounting page, whose header names it; this only holds the loading state.
function SettingsSection({ loading, children }: { loading: boolean; children: ReactNode }) {
  return <section>{loading ? <LoadingState label="Loading settings..." /> : children}</section>;
}

function StatusCard({
  live,
  liveTitle,
  idleTitle,
  liveText,
  idleText,
  canEdit,
  children,
}: {
  live: boolean;
  canEdit: boolean;
  liveTitle: string;
  idleTitle: string;
  liveText: string;
  idleText: string;
  children: ReactNode;
}) {
  return (
    <div className="@container rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <div className="flex items-start gap-3">
        <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", live ? "bg-emerald-500" : "bg-amber-500")} />
        <div className="min-w-0">
          <div className="font-semibold">{live ? liveTitle : idleTitle}</div>
          <p className="mt-1 text-sm text-muted-foreground">{live ? liveText : idleText}</p>
        </div>
      </div>
      <div className="mt-5 grid gap-4 @md:grid-cols-2">{children}</div>
      {!canEdit ? <p className="mt-4 text-xs text-muted-foreground">Only an Owner or Admin can change these.</p> : null}
    </div>
  );
}

// An unset account is what stops pushes, so it is outlined rather than left as a quiet placeholder.
function AccountPicker({
  id,
  label,
  hint,
  value,
  accounts,
  emptyLabel = "No accounts synced",
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value?: string | null;
  accounts: LedgerAccount[];
  emptyLabel?: string;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-2">
        {label}
        {!value ? (
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Not set</span>
        ) : null}
      </Label>
      <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled || accounts.length === 0}>
        <SelectTrigger id={id} className={cn("w-full", !value && "border-amber-500/60")}>
          <SelectValue placeholder={accounts.length === 0 ? emptyLabel : "Choose an account"} />
        </SelectTrigger>
        <SelectContent>
          {accounts.map((a) => (
            <SelectItem key={a.id} value={a.id!}>
              {accountLabel(a)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export function accountLabel(account: LedgerAccount) {
  return `${account.displayId ? `${account.displayId} · ` : ""}${account.name ?? "Unnamed"}`;
}

export function taxRateLabel(rate: PlatformTaxRate) {
  // QuickBooks' code is its internal numeric id: meaningless to a person, so left out.
  return `${rate.code && !/^\d+$/.test(rate.code) ? `${rate.code} · ` : ""}${rate.name ?? "Unnamed"}${
    rate.rate != null ? ` · ${rate.rate}%` : ""
  }`;
}

// Mirrors the server: a bank or credit card account, or one the platform gave no type for.
export function isPaymentAccount(account: LedgerAccount) {
  const type = account.accountType?.toLowerCase();
  return !type || type === "bank" || type === "credit_card";
}

